import {useEffect, useState} from 'react'
import styled from 'styled-components'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import {
    AdminCoreApi,
    AuditEvent,
    AuditEventFilters,
} from '../../services/AdminCoreApi.service'
import {getCurrentColors} from '../../utils/colors'

const COLORS = getCurrentColors()
const ACCENT = COLORS?.primariaEscura || '#3394EF'

const POR_PAGINA = 50

// Valores reais gravados pela trilha do core (CREATE/UPDATE/DELETE).
const OPERACAO_CONFIG: Record<string, {cor: string}> = {
    CREATE: {cor: '#23CB69'},
    UPDATE: {cor: '#FFA500'},
    DELETE: {cor: '#FF4D4D'},
}

const Container = styled.div`
    flex: 1;
    height: 100vh;
    overflow-y: auto;
    padding: 28px 40px;
    background-color: var(--surface-page);
    @media (max-width: 640px) {
        padding: 18px 14px;
    }
`
const Card = styled.div`
    background: #fff;
    border: 1px solid #e6e9ee;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 22px;
`
const Title = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: #1f2b3a;
    margin: 0 0 18px 0;
`
const Filtros = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    margin-bottom: 18px;
`
const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #7a8896;
    text-transform: uppercase;
    padding: 8px 10px;
    border-bottom: 1px solid #eef1f4;
    white-space: nowrap;
`
const Td = styled.td`
    padding: 8px 10px;
    border-bottom: 1px solid #f1f3f6;
    vertical-align: top;
    font-size: 13px;
`
const Row = styled.tr`
    cursor: pointer;
    &:hover {
        background-color: #f6f8fa;
    }
`
const Chip = styled.span<{$cor: string}>`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 100px;
    background-color: ${({$cor}) => $cor};
    color: #fff;
    font-size: 12px;
    font-weight: 600;
`

const quando = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR') : '—'

const curto = (uuid: string | null) => (uuid ? uuid.slice(0, 8) : null)

const mensagemErro = (status?: number) => {
    if (status === 503) return 'Banco de auditoria indisponível.'
    if (status === 401 || status === 403)
        return 'Acesso restrito a administradores.'
    return 'Falha ao carregar eventos de auditoria.'
}

const preStyle = {overflow: 'auto', maxHeight: 320, fontSize: 12} as const

export default function Auditoria() {
    const [eventos, setEventos] = useState<AuditEvent[]>([])
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState<string | null>(null)
    const [pagina, setPagina] = useState(1)
    const [selecionado, setSelecionado] = useState<AuditEvent | null>(null)

    const [entity, setEntity] = useState('')
    const [entityId, setEntityId] = useState('')
    const [operation, setOperation] = useState('')
    const [dataDe, setDataDe] = useState('')
    const [dataAte, setDataAte] = useState('')
    // Só o botão "Filtrar" dispara nova busca — não a cada tecla.
    const [versaoFiltro, setVersaoFiltro] = useState(0)

    useEffect(() => {
        let ativo = true
        const params: AuditEventFilters = {
            skip: (pagina - 1) * POR_PAGINA,
            limit: POR_PAGINA,
        }
        if (entity) params.entity = entity
        if (entityId) params.entity_id = entityId
        if (operation) params.operation = operation
        // Converte o dia local para instante UTC; sem isso o filtro erra o fuso.
        if (dataDe)
            params.occurred_from = new Date(`${dataDe}T00:00:00`).toISOString()
        if (dataAte)
            params.occurred_to = new Date(
                `${dataAte}T23:59:59.999`
            ).toISOString()

        setCarregando(true)
        setErro(null)
        new AdminCoreApi()
            .listAuditEvents(params)
            .then((data) => {
                if (!ativo) return
                setEventos(data || [])
            })
            .catch((e: unknown) => {
                if (!ativo) return
                const status = (e as {response?: {status?: number}})?.response
                    ?.status
                setErro(mensagemErro(status))
                setEventos([])
            })
            .finally(() => {
                if (ativo) setCarregando(false)
            })

        return () => {
            ativo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagina, versaoFiltro])

    const filtrar = () => {
        setPagina(1)
        setVersaoFiltro((v) => v + 1)
    }

    return (
        <Container>
            <Title>Auditoria</Title>

            <Card>
                <Filtros>
                    <TextField
                        size="small"
                        label="Entidade"
                        placeholder="Ex.: User (exato)"
                        value={entity}
                        onChange={(e) => setEntity(e.target.value)}
                    />
                    <TextField
                        size="small"
                        label="ID da entidade"
                        placeholder="guid exato"
                        value={entityId}
                        onChange={(e) => setEntityId(e.target.value)}
                    />
                    <TextField
                        select
                        size="small"
                        label="Operação"
                        value={operation}
                        onChange={(e) => setOperation(e.target.value)}
                        sx={{minWidth: 140}}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="CREATE">CREATE</MenuItem>
                        <MenuItem value="UPDATE">UPDATE</MenuItem>
                        <MenuItem value="DELETE">DELETE</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        type="date"
                        label="De"
                        InputLabelProps={{shrink: true}}
                        value={dataDe}
                        onChange={(e) => setDataDe(e.target.value)}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="Até"
                        InputLabelProps={{shrink: true}}
                        value={dataAte}
                        onChange={(e) => setDataAte(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        onClick={filtrar}
                        sx={{backgroundColor: ACCENT}}
                    >
                        Filtrar
                    </Button>
                </Filtros>

                {erro && (
                    <Alert
                        severity="error"
                        onClose={() => setErro(null)}
                        sx={{marginBottom: '14px'}}
                    >
                        {erro}
                    </Alert>
                )}

                <Typography
                    variant="body2"
                    sx={{color: '#7a8896', marginBottom: '10px'}}
                >
                    Autor e organização são de quem executou a ação, não
                    necessariamente da organização afetada.
                </Typography>

                {carregando ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', padding: '32px'}}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr>
                                    <Th>Quando</Th>
                                    <Th>Módulo</Th>
                                    <Th>Entidade</Th>
                                    <Th>ID da entidade</Th>
                                    <Th>Operação</Th>
                                    <Th>Autor</Th>
                                    <Th>Org do autor</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {eventos.length === 0 ? (
                                    <tr>
                                        <Td colSpan={7}>
                                            Nenhum evento de auditoria encontrado.
                                        </Td>
                                    </tr>
                                ) : (
                                    eventos.map((evento) => (
                                        <Row
                                            key={evento.event_id}
                                            onClick={() => setSelecionado(evento)}
                                        >
                                            <Td>{quando(evento.occurred_at)}</Td>
                                            <Td>{evento.module || '—'}</Td>
                                            <Td>{evento.entity || '—'}</Td>
                                            <Td title={evento.entity_id || ''}>
                                                {evento.entity_id || '—'}
                                            </Td>
                                            <Td>
                                                {evento.operation &&
                                                OPERACAO_CONFIG[evento.operation] ? (
                                                    <Chip
                                                        $cor={
                                                            OPERACAO_CONFIG[
                                                                evento.operation
                                                            ].cor
                                                        }
                                                    >
                                                        {evento.operation}
                                                    </Chip>
                                                ) : (
                                                    evento.operation || '—'
                                                )}
                                            </Td>
                                            <Td title={evento.user_uuid || ''}>
                                                {evento.user_label ||
                                                    curto(evento.user_uuid) ||
                                                    'sistema/serviço'}
                                            </Td>
                                            <Td title={evento.org_uuid || ''}>
                                                {curto(evento.org_uuid) || '—'}
                                            </Td>
                                        </Row>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        marginTop: '16px',
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={pagina === 1}
                        onClick={() => setPagina((p) => p - 1)}
                    >
                        Anterior
                    </Button>
                    <Typography variant="body2">Página {pagina}</Typography>
                    <Button
                        variant="outlined"
                        disabled={eventos.length < POR_PAGINA}
                        onClick={() => setPagina((p) => p + 1)}
                    >
                        Próxima
                    </Button>
                </Box>
            </Card>

            <Dialog
                open={!!selecionado}
                onClose={() => setSelecionado(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Evento de auditoria</DialogTitle>
                <DialogContent dividers>
                    {selecionado && (
                        <>
                            <Typography variant="body2">
                                <strong>Quando:</strong>{' '}
                                {quando(selecionado.occurred_at)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Recebido em:</strong>{' '}
                                {quando(selecionado.received_at)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Módulo:</strong>{' '}
                                {selecionado.module || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Entidade:</strong>{' '}
                                {selecionado.entity || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>ID da entidade:</strong>{' '}
                                {selecionado.entity_id || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Operação:</strong>{' '}
                                {selecionado.operation || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Autor:</strong>{' '}
                                {selecionado.user_label ||
                                    selecionado.user_uuid ||
                                    'sistema/serviço'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Org do autor:</strong>{' '}
                                {selecionado.org_uuid || '—'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>event_id:</strong> {selecionado.event_id}
                            </Typography>
                            <Typography variant="body2">
                                <strong>trace_id:</strong>{' '}
                                {selecionado.trace_id || '—'}
                            </Typography>

                            <Typography variant="subtitle2" sx={{marginTop: '16px'}}>
                                Antes
                            </Typography>
                            {selecionado.before ? (
                                <pre style={preStyle}>
                                    {JSON.stringify(selecionado.before, null, 2)}
                                </pre>
                            ) : (
                                <Typography variant="body2">—</Typography>
                            )}

                            <Typography variant="subtitle2" sx={{marginTop: '16px'}}>
                                Depois
                            </Typography>
                            {selecionado.after ? (
                                <pre style={preStyle}>
                                    {JSON.stringify(selecionado.after, null, 2)}
                                </pre>
                            ) : (
                                <Typography variant="body2">—</Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelecionado(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
